import React, {
  RefObject, useRef,
} from 'react';
import {
  Form, Table, Input,
} from 'formik-antd';
import {
  FormikProvider, FieldArray, ArrayHelpers, FormikValues, useFormik,
} from 'formik';
import * as Yup from 'yup';
import { Popconfirm } from 'antd';

import FieldArrayRef from '../../../utils/FieldArrayRef';
import PrimaryButton from '../../lib/primary-button';
import { ReactComponent as ThrashCan } from '../../../assets/icons/thrashCan.svg';

const { TextArea } = Input;

const buildZonesTableValidationRules = () => Yup.object().shape({
  zones: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Zone name is required'),
      logicalReadersAsText: Yup.string()
        .nullable()
        .test(
          'no-spaces',
          'Logical readers cannot contain spaces',
          (value) => !value || !/\s/.test(value),
        ),
    }),
  ),
});

interface IZoneListFormProps {
  // eslint-disable-next-line
  zones: any[];
  loading?: boolean;
  onSubmit?: (values: FormikValues) => void;
  // eslint-disable-next-line
  fieldArrayRef: RefObject<any>;
  currentZonesFilter: string;
  searchZonesValue: string;
}

interface IZoneFormValues {
  zones: Array<{
    name: string;
    propertiesAsText?: string;
    logicalReadersAsText?: string;
    polygonsAsText?: string;
    id?: string;
  }>;
}

const ZoneListForm = ({
  zones, loading, onSubmit, fieldArrayRef, currentZonesFilter, searchZonesValue,
}: IZoneListFormProps): JSX.Element => {
  // eslint-disable-next-line
  const formikRef = useRef<any>(null);

  const formik = useFormik<IZoneFormValues>({
    initialValues: { zones },
    validationSchema: buildZonesTableValidationRules(),
    onSubmit: (values: FormikValues) => {
      if (onSubmit) onSubmit(values);
    },
    enableReinitialize: true,
  });

  const { errors, dirty, isValid } = formik;

  // eslint-disable-next-line
  const zonesColumns = [{
    title: 'Zone',
    dataIndex: 'name',
    key: 'name',
    width: 260,
    // eslint-disable-next-line
    render: (text: string, record: any, i: number) => {
      const zoneError = errors?.zones?.[i] as { name?: string } | undefined;
      return (
        <>
          <Input
            className="field__row-input"
            name={`zones.${record.originalIndex}.name`}
          />
          {zoneError?.name && (
            <span style={{ color: 'red' }}>
              (
              {zoneError?.name}
              )
            </span>
          )}
        </>
      );
    },
  }, {
    title: 'Properties',
    dataIndex: 'properties',
    key: 'properties',
    // eslint-disable-next-line
    render: (text: string, record: any, i: number) => {
      return <TextArea name={`zones.${i}.propertiesAsText`} autoSize />;
    },
  }, {
    title: 'Logical readers',
    key: 'logicalReaders',
    // eslint-disable-next-line
    render: (text: string, record: any, i: number) => {
      const zoneError = errors?.zones?.[i] as { logicalReadersAsText?: string } | undefined;
      return (
        <>
          <TextArea name={`zones.${i}.logicalReadersAsText`} autoSize />
          {zoneError?.logicalReadersAsText && (
            <span style={{ color: 'red' }}>
              (
              {zoneError?.logicalReadersAsText}
              )
            </span>
          )}
        </>
      );
    },
  }, {
    title: 'Polygons',
    key: 'polygons',
    // eslint-disable-next-line
    render: (text: string, record: any, i: number) => (
      <TextArea name={`zones.${i}.polygonsAsText`} autoSize />
    ),
  }, {
    title: 'Delete',
    key: 'delete',
    // eslint-disable-next-line
    render: (text: string, record: any, i: number) => (
      <Popconfirm
        title="Are you sure you want to delete this record/zone?"
        onConfirm={() => {
          fieldArrayRef.current?.getFieldArrayHelpers().remove(i);
          formikRef.current?.validateForm();
        }}
      >
        <button type="button">
          <ThrashCan />
        </button>
      </Popconfirm>
    ),
  }];

  const filteredZonesWithIndex = formik.values.zones
    .map((zone, index) => ({ ...zone, originalIndex: index }))
    .filter((zone) => {
      if (!currentZonesFilter) return true;
      const key = currentZonesFilter.toLowerCase();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (zone as any)[key];
      return value?.toLowerCase().includes(searchZonesValue.toLowerCase());
    });

  return (
    <FormikProvider value={formik}>
      <Form>
        <FieldArray name="zones">
          {(arrayHelpers: ArrayHelpers) => (
            <FieldArrayRef
              ref={fieldArrayRef}
              fieldArrayHelpers={arrayHelpers}
              dirty={dirty}
              values={formik.values}
            >
              <Table
                className="table zone-table"
                name="zones"
                columns={zonesColumns}
                rowKey={(record, index) => record.id || index}
                bordered={false}
                loading={false}
                pagination={false}
                dataSource={filteredZonesWithIndex}
              />
              <PrimaryButton
                type="submit"
                className="fields-table__button-next"
                style={{ width: 280 }}
                disabled={loading || !isValid || !dirty}
                loading={loading}
              >
                Save changes
              </PrimaryButton>
            </FieldArrayRef>
          )}
        </FieldArray>
      </Form>
    </FormikProvider>
  );
};

export default ZoneListForm;
